import { get, set } from '@ember/object';
import Service, { type Registry as Services, service } from '@ember/service';
import { htmlSafe } from '@ember/template';
import { tracked } from '@glimmer/tracking';
import type { Hotel } from 'frontend-utils/models';
import {
  CURRENCY_MAPPING,
  type SupportedCurrencies,
} from 'frontend-utils/utils/constants/currency-mapping-constants';
import type { Rate } from 'lumen/models';
import type { PeriodParity, PeriodRate } from 'lumen/models/period';
import type { HotelOrMarket, RateLike } from 'lumen/services/hotel-and-market';
import {
  MEALTYPE_TRANSLATION_KEYS,
  MEMBERSHIPTYPE_TRANSLATION_KEYS,
  RATE_NO_VALUE,
  THIRD_PARTY_LABEL,
} from 'lumen/utils/constants/general-constants';

export type FormatOptions = {
  maximumFractionDigits: number;
  minimumFractionDigits: number;
  useGrouping?: boolean;
};

export type FormatRateOptions = {
  format?: FormatOptions;
  includeCurrencySymbol?: boolean;
  includeSign?: boolean;
  longFormat?: boolean;
  useOverviewRevenueCurrency?: boolean;
  useRevenueInsightCurrency?: boolean;
};

export type FormatRateChangeOptions = Pick<
  FormatRateOptions,
  'longFormat' | 'format' | 'includeSign'
>;

type FormatValueOptions = {
  currencySymbol: string | undefined;
  decimals?: boolean;
  format?: FormatOptions;
  includeSign?: boolean;
  longFormat: boolean;
  useShortNotation?: boolean;
};

const SHORT_NOTATION_CURRENCIES = ['IDR', 'KRW', 'VND'];

export default class RatesService extends Service {
  @service declare access: Services['access'];
  @service declare intl: Services['intl'];

  @tracked relevantRentalProperties = 0;

  cachedLosRestrictionRates: Record<string, Record<number, Set<unknown>>> = {};

  clearCachedLosRestrictionRates(): void {
    set(this, 'cachedLosRestrictionRates', {});
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  formatRate(
    rate: number | RateLike | null,
    hotelOrMarket: HotelOrMarket | null = null,
    {
      includeCurrencySymbol = true,
      longFormat = true,
      format = {
        maximumFractionDigits: 0,
        minimumFractionDigits: 0,
      },
      includeSign = false,
      useRevenueInsightCurrency = false,
      useOverviewRevenueCurrency = false,
    }: FormatRateOptions = {},
  ) {
    if (rate === null) {
      return null;
    }

    if (rate || Number.isFinite(rate)) {
      let value = 0;
      if (typeof rate === 'number') {
        value = rate;
      } else {
        if (rate.value === RATE_NO_VALUE && rate.message) {
          if (this.intl.exists(rate.message)) {
            return this.intl.t(rate.message);
          }
          return this.intl.t('general.missing');
        }
        value = rate.value;
      }
      let currencySymbol: string | undefined;

      if (includeCurrencySymbol) {
        currencySymbol = this.getCurrencySymbol(
          rate,
          hotelOrMarket,
          useRevenueInsightCurrency,
          useOverviewRevenueCurrency,
        );
      }
      const decimals = this.access.hasFeature('settings.show_decimals');

      const currencyCode = this.getCurrencyCode(
        rate,
        hotelOrMarket,
        useRevenueInsightCurrency,
        useOverviewRevenueCurrency,
      );
      const useShortNotation = SHORT_NOTATION_CURRENCIES.includes(currencyCode);

      return this.formatValue(value, {
        longFormat,
        currencySymbol,
        format,
        decimals,
        useShortNotation,
        includeSign,
      });
    }
    return null;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  formatRateChange(
    rate: Rate | number,
    currency: SupportedCurrencies,
    {
      longFormat = true,
      format = {
        maximumFractionDigits: 0,
        minimumFractionDigits: 0,
      },
      includeSign = false,
    }: FormatRateChangeOptions = {},
  ) {
    if (rate) {
      let value = 0;
      if (typeof rate === 'number') {
        value = rate;
      } else {
        if (rate.value === RATE_NO_VALUE && rate.message) {
          return this.intl.t(rate.message);
        }
        value = rate.value;
      }

      const currencySymbol = CURRENCY_MAPPING[currency];

      const decimals = this.access.hasFeature('settings.show_decimals');
      const useShortNotation = SHORT_NOTATION_CURRENCIES.includes(currency);

      return this.formatValue(value, {
        longFormat,
        currencySymbol,
        format,
        decimals,
        useShortNotation,
        includeSign,
      });
    }
    return null;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  formatRateObject(
    rate: Rate | PeriodRate | PeriodParity,
    includeCurrencySymbol = true,
    longFormat = true,
    format: FormatOptions = {
      maximumFractionDigits: 0,
      minimumFractionDigits: 0,
    },
  ) {
    if (rate) {
      let value = 0;
      if (rate.value === RATE_NO_VALUE && rate.message) {
        return this.intl.t(rate.message);
      }
      if (!rate.id) {
        return this.intl.t('general.missing');
      }
      value = rate.value;

      let currencySymbol;

      if (includeCurrencySymbol) {
        if (CURRENCY_MAPPING[rate.currency]) {
          currencySymbol = CURRENCY_MAPPING[rate.currency];
        } else {
          currencySymbol = rate.currency;
        }
      }

      return this.formatValue(value, {
        longFormat,
        currencySymbol,
        format,
      });
    }
    return null;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  formatValue(
    valueParam: number,
    {
      longFormat,
      currencySymbol,
      format,
      decimals,
      useShortNotation,
      includeSign,
    }: FormatValueOptions,
  ) {
    let formattedValue = '0';
    let value = valueParam;
    let absValue = Math.abs(value);
    const willAbbreviate = useShortNotation && !longFormat && absValue > 1000; // false; //value > 100000 && !longFormat;

    if (willAbbreviate) {
      value /= 1000;
      absValue /= 1000;
    }

    if (valueParam) {
      const valueToUse = includeSign ? absValue : value;
      let formatToUse = Object.assign({}, format);

      if (decimals) {
        formatToUse = {
          ...formatToUse,
          maximumFractionDigits: 2,
          minimumFractionDigits: 2,
        };
      }

      formattedValue = this.intl.formatNumber(valueToUse, formatToUse);
    }

    if (willAbbreviate) {
      formattedValue += 'k';
    }

    if (currencySymbol) {
      // Note: rate must be an object
      // HTML entities are needed to prevent arabic currencies displaying right to left
      formattedValue = `&rlm;${currencySymbol}&lrm; ${formattedValue}`;
    }

    if (includeSign) {
      if (valueParam > 0) {
        formattedValue = `+${formattedValue}`;
      } else if (valueParam < 0) {
        formattedValue = `-${formattedValue}`;
      }
    }

    return htmlSafe(formattedValue) as unknown as string;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  getCachedLosRestrictionRates(fromDate: string, los: number) {
    const ratesSet = this.cachedLosRestrictionRates[fromDate]?.[los];
    if (ratesSet) {
      // ratesSet is a Set object, but we need it to be an array later on
      // This line converts this set to an array
      return [...ratesSet];
    }
    return [];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  getCurrencyCode(
    rate: number | { currency: SupportedCurrencies },
    hotelOrMarket: HotelOrMarket | null,
    useRevenueInsightCurrency = false,
    useOverviewRevenueCurrency = false,
  ) {
    if (
      useOverviewRevenueCurrency &&
      (hotelOrMarket as Hotel)?.overviewRevenueCurrency
    ) {
      return (hotelOrMarket as Hotel).overviewRevenueCurrency;
    }
    if (useRevenueInsightCurrency && hotelOrMarket?.revenueInsightCurrency) {
      return hotelOrMarket.revenueInsightCurrency;
    }
    if (hotelOrMarket?.currencyCode) {
      return hotelOrMarket.currencyCode;
    }
    if (typeof rate !== 'number') {
      return rate.currency;
    }

    return '';
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  getCurrencySymbol(
    rate: number | { currency: SupportedCurrencies },
    hotelOrMarket: HotelOrMarket | null,
    useRevenueInsightCurrency = false,
    useOverviewRevenueCurrency = false,
  ) {
    if (
      useOverviewRevenueCurrency &&
      (hotelOrMarket as Hotel)?.overviewRevenueCurrency
    ) {
      return CURRENCY_MAPPING[(hotelOrMarket as Hotel).overviewRevenueCurrency];
    }
    if (useRevenueInsightCurrency && hotelOrMarket?.revenueInsightCurrency) {
      return CURRENCY_MAPPING[hotelOrMarket.revenueInsightCurrency];
    }
    if (hotelOrMarket && (hotelOrMarket as Hotel).currencySymbol) {
      return (hotelOrMarket as Hotel).currencySymbol;
    }
    if (hotelOrMarket && hotelOrMarket.currencyCode) {
      return CURRENCY_MAPPING[hotelOrMarket.currencyCode];
    }

    if (typeof rate !== 'number') {
      if (CURRENCY_MAPPING[rate.currency]) {
        return CURRENCY_MAPPING[rate.currency];
      }
      return rate.currency;
    }

    return '';
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  getRateTypeString(rate: Rate | PeriodRate | undefined) {
    if (rate !== undefined) {
      const rateTypeStrings = [];

      // const cancellationString = rate.best_flex ? get(this, 'intl').t('rates.bar_short') : get(this, 'intl').t('rates.lowest');

      if ((rate as PeriodRate).labels?.indexOf(THIRD_PARTY_LABEL) > -1) {
        rateTypeStrings.push(this.intl.t('rates.third_party'));
      }

      // Fix for when the rate is a Ember object (camelCase instead of snake_case)
      const mealtype =
        (rate as PeriodRate).mealtype_included ||
        (rate as Rate).mealtypeIncluded;

      if (mealtype) {
        rateTypeStrings.push(this.intl.t(MEALTYPE_TRANSLATION_KEYS[mealtype]));
      }

      if (rate.membershiptype > 0) {
        rateTypeStrings.push(
          this.intl.t(MEMBERSHIPTYPE_TRANSLATION_KEYS[rate.membershiptype]),
        );
      }

      return rateTypeStrings.join(', ');
    }
    return '';
  }

  setCachedLosRestrictionRates(rates: Rate[]): void {
    rates.forEach((rate) => {
      const { los } = rate.period;
      const fromDate = rate.period.fromDate.format('YYYY-MM-DD');
      if (!get(this.cachedLosRestrictionRates, fromDate)) {
        set(this.cachedLosRestrictionRates, fromDate, {});
      }

      if (!get(this, `cachedLosRestrictionRates.${fromDate}.${los}`)) {
        set(
          this.cachedLosRestrictionRates,
          `${fromDate}.${los}`,
          new Set([rate]),
        );
      } else {
        this.cachedLosRestrictionRates[fromDate]?.[los]?.add(rate);
      }
    });
  }
}

declare module '@ember/service' {
  interface Registry {
    rates: RatesService;
  }
}
